import { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Eraser, Check, Type, PenTool } from 'lucide-react';
import { Input } from './ui/input';

export const SignaturePad = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mode, setMode] = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState('');
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#0f0f10';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#fafafa';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0f0f10';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setTypedName('');
  };

  const renderTypedSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0f0f10';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (typedName) {
      ctx.font = 'italic 48px Georgia, serif';
      ctx.fillStyle = '#fafafa';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
      setHasSignature(true);
    }
  };

  useEffect(() => {
    if (mode === 'type') {
      renderTypedSignature();
    }
  }, [typedName, mode]);

  const handleSave = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave({
      signature_data: dataUrl,
      signature_type: mode === 'draw' ? 'drawn' : 'typed',
    });
  };

  return (
    <div className="space-y-4" data-testid="signature-pad">
      {/* Mode toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === 'draw' ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setMode('draw');
            clearCanvas();
          }}
          className={mode === 'draw' ? 'bg-blue-600 hover:bg-blue-500' : 'border-zinc-700'}
          data-testid="draw-mode-btn"
        >
          <PenTool className="w-4 h-4 mr-2" />
          Draw
        </Button>
        <Button
          type="button"
          variant={mode === 'type' ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setMode('type');
            clearCanvas();
          }}
          className={mode === 'type' ? 'bg-blue-600 hover:bg-blue-500' : 'border-zinc-700'}
          data-testid="type-mode-btn"
        >
          <Type className="w-4 h-4 mr-2" />
          Type
        </Button>
      </div>

      {/* Type input */}
      {mode === 'type' && (
        <Input
          placeholder="Type your full legal name"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          className="bg-zinc-950 border-zinc-800 text-zinc-200"
          data-testid="typed-signature-input"
        />
      )}

      {/* Canvas */}
      <div
        className={`signature-container ${isDrawing ? 'signing' : ''}`}
      >
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          className="w-full h-auto signature-canvas rounded"
          onMouseDown={mode === 'draw' ? startDrawing : undefined}
          onMouseMove={mode === 'draw' ? draw : undefined}
          onMouseUp={mode === 'draw' ? stopDrawing : undefined}
          onMouseLeave={mode === 'draw' ? stopDrawing : undefined}
          onTouchStart={mode === 'draw' ? startDrawing : undefined}
          onTouchMove={mode === 'draw' ? draw : undefined}
          onTouchEnd={mode === 'draw' ? stopDrawing : undefined}
          data-testid="signature-canvas"
        />
        {mode === 'draw' && !hasSignature && (
          <p className="text-xs text-zinc-500 text-center mt-2">
            Draw your signature above
          </p>
        )}
      </div>

      {/* Legal notice */}
      <p className="text-xs text-zinc-500">
        By signing this document, you agree that your electronic signature is the
        legal equivalent of your manual signature and is legally binding under the
        U.S. ESIGN Act and UETA.
      </p>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={clearCanvas}
          className="border-zinc-700 hover:bg-zinc-800"
          data-testid="clear-signature-btn"
        >
          <Eraser className="w-4 h-4 mr-2" />
          Clear
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="hover:bg-zinc-800"
            data-testid="cancel-signature-btn"
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSave}
          disabled={!hasSignature}
          className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
          data-testid="save-signature-btn"
        >
          <Check className="w-4 h-4 mr-2" />
          Apply Signature
        </Button>
      </div>
    </div>
  );
};
